import React, {
    createContext,
    useCallback,
    useEffect,
    useState,
    useMemo,
} from 'react';
import { useMetadataContext } from 'utils/contexts/MetadataContext';
import { useGet } from 'utils/hooks/useAPI';
import messageBroker from 'utils/services/messageBroker';
import { ApiError } from 'utils/services/heliosAPI';
import { Account, AuthContextType, AuthState } from './AuthContextType';
import { Auth0Context } from './Auth0Context';
import { LocalAuthContext } from './LocalAuthContext';
import rg4js from 'raygun4js';
import { setZendeskUserInfo } from 'utils/hooks/useZendeskWidget';
import { isEnvOpAccount } from 'utils/isEnvOpAccount';

// non-null assertion is covered in useAuthContext hook below
export const AuthContext = createContext<AuthContextType>(null!);

interface AuthProviderProps extends React.PropsWithChildren {
    auth0?: boolean;
}

const ACCOUNT_URL = '/helios-api/auth/account';

export const AuthProvider = ({ children }: AuthProviderProps) => {
    const metadata = useMetadataContext();
    const [authState, setAuthState] = useState<AuthState>(AuthState.UNKNOWN);
    // non-null assertion here that makes it easier for authenticated pages to handle the account object
    const [account, setAccount] = useState<Account>(undefined!);

    const [, getAccount] = useGet<Account>(ACCOUNT_URL, true);

    const isEnvOp = useMemo(
        () => account && isEnvOpAccount(account?.proxy_user || account),
        [account]
    );

    useEffect(() => {
        if (account) {
            rg4js('setUser', {
                identifier: `${account.account_id}`,
                isAnonymous: false,
                email: account.email,
                fullName: account.full_name,
            });
            setZendeskUserInfo(account);
        }
    }, [account]);

    useEffect(() => {
        const unsubscribe = messageBroker.subscribe('unauthorizedError', () => {
            setAuthState(AuthState.UNAUTHENTICATED);
        });

        return () => {
            unsubscribe();
        };
    }, []);

    const loadAccount = useCallback(async () => {
        try {
            const response = await getAccount();
            setAccount(response);
            setAuthState(AuthState.AUTHENTICATED);
        } catch (e) {
            const error = e as ApiError;
            const status = error?.response?.status;

            if (status == 401) {
                setAuthState(AuthState.UNAUTHENTICATED);
            } else if (status) {
                setAuthState(AuthState.ERROR);
            } else {
                setAuthState(AuthState.UNKNOWN);
            }
        }
    }, [getAccount]);

    const authContext = metadata.auth0_enabled
        ? Auth0Context()
        : LocalAuthContext();

    const goToLogin = authContext.goToLogin;
    const login = authContext.login;
    const logout = (path?: string) => {
        rg4js('endSession');
        authContext.logout(!!account?.proxy_user, path);
    };

    return (
        <AuthContext
            value={{
                goToLogin,
                login,
                logout,
                authState,
                account,
                loadAccount,
                isEnvOp,
            }}
        >
            {children}
        </AuthContext>
    );
};

export const useAuthContext = () => {
    const authContext = React.useContext(AuthContext);

    if (!authContext) {
        throw new Error('useAuthContext must be used within an AuthProvider');
    }

    return authContext;
};
